import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Mail, Phone, Globe, Award, Users, MapPin, Calendar, DollarSign, Star } from 'lucide-react';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';


const MuseumProposalInner = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-900 via-red-900 to-amber-900 print:bg-white print:text-black">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-amber-200 hover:text-white print:hidden">
            <ArrowLeft className="w-5 h-5" />
            Back to Escola de Samba Casa Samba
          </Link>
          <button 
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-colors print:hidden"
          >
            <Download className="w-4 h-4" />
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 print:py-4 print:text-black print:bg-white">
        
        {/* Title Section */}
        <div className="text-center mb-12 print:mb-8">
          <p className="text-amber-400 text-sm tracking-widest mb-4 print:text-gray-600">
            <PageEditable path="hero.eyebrow" fallback="PROPOSAL" />
          </p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-400 via-red-400 to-amber-400 bg-clip-text text-transparent print:text-black">
            <PageEditable path="hero.title" fallback="Carnival Cultures Interactive Tour Museum" />
          </h1>
          <p className="text-xl text-gray-300 print:text-gray-700">
            <PageEditable path="hero.subtitle" fallback="Presented by Escola de Samba Casa Samba" />
          </p>
        </div>

        {/* Submission Info */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 mb-10 print:bg-gray-100 print:border print:border-gray-300">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-gray-400 text-sm mb-1 print:text-gray-600">Submitted to:</p>
              <p className="text-white font-semibold print:text-black">City of New Orleans Recreation Department (NORDC)</p>
              <p className="text-gray-300 print:text-gray-700">Annunciation Center</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1 print:text-gray-600">Submitted by:</p>
              <p className="text-white font-semibold print:text-black">Mestre Curtis Pierre</p>
              <p className="text-gray-300 print:text-gray-700">Founder & Cultural Director, Escola de Samba Casa Samba</p>
              <p className="text-amber-400 print:text-amber-700">Ambassador of Afro-Brasilian Culture</p>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <Star className="w-6 h-6" />
            <PageEditable path="s.execSummary.heading" as="span" fallback="Executive Summary" />
          </h2>
          <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
            <p className="text-gray-300 leading-relaxed mb-4 print:text-gray-700">
              <PageEditable path="s.execSummary.p1" as="span" multiline fallback="Escola de Samba Casa Samba respectfully proposes to establish the Carnival Cultures Interactive Tour Museum at the Annunciation Center—a first-of-its-kind cultural institution highlighting the Afro-diasporic carnival traditions that have shaped New Orleans into the unique cultural melting pot it is today." />
            </p>
            <p className="text-gray-300 leading-relaxed mb-4 print:text-gray-700">
              <PageEditable path="s.execSummary.p2" as="span" multiline fallback="The Carnival Cultural Museum will showcase the dance, drumming styles, traditional masquerades, and puppetry of the diverse carnival cultures that have called New Orleans home since the 1980s, including Vietnamese, Chinese, African, Brasilian, Haitian, and Trinidadian communities." />
            </p>
            <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4 print:bg-green-50 print:border-green-300">
              <p className="text-green-400 font-semibold print:text-green-700">
                <PageEditable path="s.execSummary.callout" as="span" multiline fallback="This program requires no financial investment from NORDC. All equipment, materials, and expertise are already in place. Escola de Samba Casa Samba requests only building availability for tours outside of regular NORDC operating hours." />
              </p>
            </div>
          </div>
        </section>

        {/* About the Curator */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <Award className="w-6 h-6" />
            About the Curator: Mestre Curtis Pierre
          </h2>
          <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
            <p className="text-gray-300 leading-relaxed mb-6 print:text-gray-700">
              Mestre Curtis Pierre is uniquely qualified to curate The Carnival Cultural Museum as <strong className="text-white print:text-black">the ONLY American in history to achieve Master-level status in TWO distinct Brasilian cultural domains</strong>—Capoeira and Brasilian Percussion.
            </p>
            
            <h3 className="text-lg font-semibold text-white mb-3 print:text-black">Credentials & Achievements:</h3>
            <ul className="space-y-2 text-gray-300 mb-6 print:text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>39+ years</strong> dedicated to Afro-Brasilian cultural preservation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>Founder of Escola de Samba Casa Samba</strong> at Tulane University (1987)—proclaimed the first samba school in New Orleans by Mayor Sidney Bartholomew on July 21, 1993</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>Pioneer of Capoeira in New Orleans</strong>—first to bring this African martial art to the city</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>Published Author</strong> of "Capoeira Angola Movements," the definitive guide on Afro-Brasilian Martial Art</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>One of the only Americans</strong> to teach both Samba and Capoeira classes IN BRAZIL</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>1,000+ students taught</strong> • <strong>50+ countries performed</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>Hollywood Credits:</strong> Cultural Consultant for "Get Hard" (2015), Drum Coordinator for "Angel Heart" (1987)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 print:text-amber-700">•</span>
                <span><strong>Official Partner</strong> with Academicos do Salgueiro (Rio de Janeiro, 2024)</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-white mb-3 print:text-black">Trained by Legendary Masters:</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { name: "Jorge 'Alabe' Bezerra", desc: "Master Drummer, Rio de Janeiro (15+ years)" },
                { name: "Mestre Cobra Mansa", desc: "Capoeira Angola Master, Salvador Bahia" },
                { name: "Airto Moreira", desc: "Grammy-winning Brasilian jazz percussionist" },
                { name: "Bill Summers", desc: "Herbie Hancock's Headhunters" },
              ].map((master, i) => (
                <div key={i} className="bg-[#252525] rounded-lg p-3 print:bg-gray-200">
                  <p className="text-amber-400 font-medium print:text-amber-700">{master.name}</p>
                  <p className="text-gray-400 text-sm print:text-gray-600">{master.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Is This Needed */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 print:text-amber-700">Why Is The Carnival Cultural Museum Needed?</h2>
          <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
            <h3 className="text-lg font-semibold text-white mb-3 print:text-black">Protecting Cultural Authenticity</h3>
            <p className="text-gray-300 leading-relaxed mb-4 print:text-gray-700">
              New Orleans proudly calls itself a "melting pot," yet there is <strong className="text-red-400 print:text-red-700">no single institution</strong> that identifies, compares, and celebrates the diverse carnival cultures that make this city unique. The strong watering down of authentic traditions and the influx of cultural appropriation threatens the purity of our carnival heritage.
            </p>
            
            <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 mb-4 print:bg-amber-50 print:border-amber-300">
              <p className="text-amber-300 font-medium mb-2 print:text-amber-700">The Carnival Cultural Museum will:</p>
              <ul className="space-y-1 text-gray-300 print:text-gray-700">
                <li>• Preserve authentic carnival traditions before they are diluted or lost</li>
                <li>• Educate residents and visitors about the TRUE origins of New Orleans' cultural identity</li>
                <li>• Provide a space where people can see the differences AND similarities all in one place</li>
                <li>• Protect the legacy of communities practicing these traditions since the 1980s</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold text-white mb-3 print:text-black">Filling a Cultural Void</h3>
            <p className="text-gray-300 leading-relaxed print:text-gray-700">
              There is <strong className="text-white print:text-black">nothing like this in New Orleans or Louisiana.</strong> While individual cultural organizations exist, no institution brings them together to show how the African diaspora connects New Orleans to the world.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <Users className="w-6 h-6" />
            Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
              <h3 className="text-lg font-semibold text-green-400 mb-3 print:text-green-700">For NORDC</h3>
              <ul className="space-y-2 text-gray-300 print:text-gray-700">
                <li>• <strong>Increased traffic</strong> to Annunciation Center</li>
                <li>• <strong>Enhanced community engagement</strong></li>
                <li>• <strong>Zero cost</strong>—all provided by Escola de Samba Casa Samba</li>
                <li>• Positions NORDC as cultural preservation leader</li>
              </ul>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
              <h3 className="text-lg font-semibold text-blue-400 mb-3 print:text-blue-700">For Escola de Samba Casa Samba</h3>
              <ul className="space-y-2 text-gray-300 print:text-gray-700">
                <li>• Permanent home for cultural programming</li>
                <li>• Sustainable revenue through tours/workshops</li>
                <li>• Training ground for next generation</li>
                <li>• Expanded cultural bridge mission</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Community Impact */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <Users className="w-6 h-6" />
            Community Impact & Skills Training
          </h2>
          
          {/* Why the Community Needs This */}
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 mb-6 print:bg-red-50 print:border-red-300">
            <h3 className="text-xl font-bold text-red-400 mb-3 print:text-red-700">Why Our Community Needs This Program</h3>
            <p className="text-gray-300 leading-relaxed mb-4 print:text-gray-700">
              Every year, carnival groups across New Orleans struggle to complete their elaborate costumes and preparations. They need <strong className="text-white print:text-black">skilled volunteers</strong> but have no way to train them. Meanwhile, community members—especially youth and seniors—lack access to hands-on creative skills training that could lead to jobs, entrepreneurship, or simply meaningful participation in our cultural traditions.
            </p>
            <p className="text-gray-300 leading-relaxed print:text-gray-700">
              <strong className="text-amber-400 print:text-amber-700">This program bridges that gap.</strong> Escola de Samba Casa Samba will train community members in real, marketable skills while simultaneously providing carnival organizations with the volunteer workforce they desperately need.
            </p>
          </div>

          {/* What Casa Samba Will Provide */}
          <div className="bg-[#1a1a1a] rounded-xl p-6 mb-6 print:bg-gray-100 print:border">
            <h3 className="text-xl font-bold text-amber-400 mb-4 print:text-amber-700">What Escola de Samba Casa Samba Will Provide</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Costume & Construction Training */}
              <div className="bg-[#252525] rounded-lg p-5 print:bg-gray-200">
                <h4 className="text-lg font-semibold text-green-400 mb-3 print:text-green-700">🧵 Costume & Construction Workshop</h4>
                <p className="text-gray-400 text-sm mb-3 print:text-gray-600">Escola de Samba Casa Samba provides ALL equipment and supplies:</p>
                <ul className="space-y-2 text-gray-300 print:text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 print:text-green-700">✓</span>
                    <span><strong>Industrial Sewing Machines</strong> — Learn to operate professional equipment used by real costume shops</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 print:text-green-700">✓</span>
                    <span><strong>Hot Glue Guns & Craft Tools</strong> — Master the techniques for attaching feathers, sequins, beads, and decorations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 print:text-green-700">✓</span>
                    <span><strong>Pattern Making & Design</strong> — Create costumes from concept to completion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 print:text-green-700">✓</span>
                    <span><strong>Mask & Headdress Construction</strong> — Build traditional carnival masks and elaborate headpieces</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 print:text-green-700">✓</span>
                    <span><strong>Puppet & Float Building</strong> — Large-scale construction techniques for parade elements</span>
                  </li>
                </ul>
              </div>

              {/* Media Production Training */}
              <div className="bg-[#252525] rounded-lg p-5 print:bg-gray-200">
                <h4 className="text-lg font-semibold text-blue-400 mb-3 print:text-blue-700">🎬 Audio & Video Production Studio</h4>
                <p className="text-gray-400 text-sm mb-3 print:text-gray-600">Escola de Samba Casa Samba will house a mini recording studio where community members learn:</p>
                <ul className="space-y-2 text-gray-300 print:text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 print:text-blue-700">✓</span>
                    <span><strong>Video Recording & Editing</strong> — Document cultural events, create promotional content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 print:text-blue-700">✓</span>
                    <span><strong>Audio Recording</strong> — Capture oral histories, record traditional music</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 print:text-blue-700">✓</span>
                    <span><strong>Podcast Production</strong> — Share stories and interviews with the community</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 print:text-blue-700">✓</span>
                    <span><strong>Social Media Content</strong> — Learn to promote cultural events effectively</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 print:text-blue-700">✓</span>
                    <span><strong>Documentary Filmmaking</strong> — Preserve our cultural heritage on film</span>
                  </li>
                </ul>
              </div>

              {/* Music & Dance Training */}
              <div className="bg-[#252525] rounded-lg p-5 print:bg-gray-200">
                <h4 className="text-lg font-semibold text-purple-400 mb-3 print:text-purple-700">🥁 Drumming & Dance Instruction</h4>
                <p className="text-gray-400 text-sm mb-3 print:text-gray-600">Learn the authentic rhythms and movements from a recognized Master:</p>
                <ul className="space-y-2 text-gray-300 print:text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 print:text-purple-700">✓</span>
                    <span><strong>Brasilian Percussion</strong> — Samba, batucada, and traditional rhythms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 print:text-purple-700">✓</span>
                    <span><strong>Capoeira Angola</strong> — The original African martial art</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 print:text-purple-700">✓</span>
                    <span><strong>Samba Dance</strong> — Authentic Brasilian carnival movement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 print:text-purple-700">✓</span>
                    <span><strong>Stilt Walking</strong> — Traditional carnival performance skill</span>
                  </li>
                </ul>
              </div>

              {/* How This Helps Carnival Groups */}
              <div className="bg-[#252525] rounded-lg p-5 print:bg-gray-200">
                <h4 className="text-lg font-semibold text-amber-400 mb-3 print:text-amber-700">🎭 Direct Support for Carnival Groups</h4>
                <p className="text-gray-400 text-sm mb-3 print:text-gray-600">Trained volunteers will directly assist carnival organizations:</p>
                <ul className="space-y-2 text-gray-300 print:text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 print:text-amber-700">✓</span>
                    <span><strong>Costume Construction</strong> — Help groups complete elaborate outfits on deadline</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 print:text-amber-700">✓</span>
                    <span><strong>Float & Prop Building</strong> — Skilled labor for large-scale construction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 print:text-amber-700">✓</span>
                    <span><strong>Event Documentation</strong> — Video and photo coverage of performances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 print:text-amber-700">✓</span>
                    <span><strong>Performance Support</strong> — Trained drummers and dancers to strengthen groups</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Who Will Benefit */}
          <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
            <h3 className="text-xl font-bold text-white mb-4 print:text-black">Who Will Benefit</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-b from-amber-900/30 to-transparent rounded-lg p-4 border border-amber-500/20 print:bg-amber-50 print:border-amber-300">
                <h4 className="text-amber-400 font-bold mb-2 print:text-amber-700">Youth & Teenagers</h4>
                <ul className="text-gray-400 text-sm space-y-1 print:text-gray-600">
                  <li>• Learn marketable job skills</li>
                  <li>• After-school creative programs</li>
                  <li>• Portfolio building for college</li>
                  <li>• Mentorship from master artists</li>
                  <li>• Alternative to street activities</li>
                </ul>
              </div>
              <div className="bg-gradient-to-b from-blue-900/30 to-transparent rounded-lg p-4 border border-blue-500/20 print:bg-blue-50 print:border-blue-300">
                <h4 className="text-blue-400 font-bold mb-2 print:text-blue-700">Adults & Job Seekers</h4>
                <ul className="text-gray-400 text-sm space-y-1 print:text-gray-600">
                  <li>• Sewing & tailoring skills</li>
                  <li>• Video production training</li>
                  <li>• Event planning experience</li>
                  <li>• Small business preparation</li>
                  <li>• Networking opportunities</li>
                </ul>
              </div>
              <div className="bg-gradient-to-b from-green-900/30 to-transparent rounded-lg p-4 border border-green-500/20 print:bg-green-50 print:border-green-300">
                <h4 className="text-green-400 font-bold mb-2 print:text-green-700">Senior Citizens</h4>
                <ul className="text-gray-400 text-sm space-y-1 print:text-gray-600">
                  <li>• Share historical knowledge</li>
                  <li>• Oral history recording</li>
                  <li>• Meaningful social engagement</li>
                  <li>• Pass traditions to youth</li>
                  <li>• Stay connected to culture</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tourism Impact */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <MapPin className="w-6 h-6" />
            Tourism Industry Impact
          </h2>
          <div className="space-y-4">
            {[
              {
                title: "The Cultural Tourist",
                scenario: "A family from Chicago visits during Jazz Fest. They discover The Carnival Cultural Museum, learn about connections between Mardi Gras Indians and Brasilian Carnival, watch live drumming, try on Trinidadian masks.",
                result: "Word-of-mouth marketing, TripAdvisor reviews, return visits."
              },
              {
                title: "The Cruise Ship Stop",
                scenario: "Tour operators seek unique, authentic experiences. The Carnival Cultural Museum offers pre-arranged group tours, interactive workshops, photo opportunities with authentic costumes.",
                result: "New revenue stream, positioning New Orleans as a cultural destination."
              },
              {
                title: "The Educational Tour",
                scenario: "University anthropology classes need academic credibility with hands-on learning. The Carnival Cultural Museum provides lectures from a published author and recognized master.",
                result: "Repeat academic partnerships, research opportunities."
              },
              {
                title: "The Convention Add-On",
                scenario: "Event planners seek memorable spouse/partner programs. The Carnival Cultural Museum offers private evening tours, samba dance lessons, mask-making workshops.",
                result: "Premium pricing, hotel and convention bureau partnerships."
              }
            ].map((item, i) => (
              <div key={i} className="bg-[#1a1a1a] rounded-xl p-5 print:bg-gray-100 print:border">
                <h3 className="text-lg font-semibold text-white mb-2 print:text-black">Scenario {i + 1}: {item.title}</h3>
                <p className="text-gray-300 mb-2 print:text-gray-700">{item.scenario}</p>
                <p className="text-green-400 text-sm print:text-green-700"><strong>Result:</strong> {item.result}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Cultures */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 print:text-amber-700">Featured Cultures</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { culture: "Brasilian", elements: "Samba, batucada drumming, elaborate costumes" },
              { culture: "Trinidadian", elements: "Steelpan, mas (masquerade), calypso" },
              { culture: "Haitian", elements: "Rara, Vodou traditions, handmade masks" },
              { culture: "African", elements: "Traditional drumming, ancestral masquerades" },
              { culture: "Vietnamese", elements: "Lion dance, Tet celebrations" },
              { culture: "Chinese", elements: "Dragon dance, percussion traditions" },
            ].map((item, i) => (
              <div key={i} className="bg-[#1a1a1a] rounded-xl p-4 text-center print:bg-gray-100 print:border">
                <p className="text-amber-400 font-bold text-lg mb-1 print:text-amber-700">{item.culture}</p>
                <p className="text-gray-400 text-sm print:text-gray-600">{item.elements}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Cost */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <DollarSign className="w-6 h-6" />
            Program Costs & Sustainability
          </h2>
          <div className="bg-green-900/20 border border-green-500/50 rounded-xl p-6 mb-4 print:bg-green-50 print:border-green-300">
            <h3 className="text-2xl font-bold text-green-400 mb-2 print:text-green-700">Startup Cost to NORDC: $0</h3>
            <p className="text-gray-300 print:text-gray-700">
              Escola de Samba Casa Samba provides all equipment, supplies, expertise, and marketing. NORDC provides only building access during tour hours.
            </p>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
            <h3 className="text-lg font-semibold text-white mb-3 print:text-black">Self-Sustaining Revenue Model</h3>
            <div className="grid grid-cols-2 gap-3">
              {["Admission Fees", "Tour Packages", "Workshop Fees", "Private Events", "Gift Shop"].map((item, i) => (
                <div key={i} className="bg-[#252525] rounded-lg p-3 print:bg-gray-200">
                  <p className="text-amber-400 print:text-amber-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-amber-400 mb-4 flex items-center gap-2 print:text-amber-700">
            <Calendar className="w-6 h-6" />
            Timeline
          </h2>
          <div className="bg-[#1a1a1a] rounded-xl p-6 print:bg-gray-100 print:border">
            <div className="space-y-4">
              {[
                { phase: "Soft Opening", date: "August 2026", desc: "Initial exhibit installation, invite-only preview" },
                { phase: "Community Preview", date: "September 2026", desc: "Local schools and organizations" },
                { phase: "Grand Opening", date: "October 2026", desc: "Public launch, media coverage" },
                { phase: "Full Operations", date: "November 2026", desc: "Regular tour schedule, workshop calendar" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-32 text-amber-400 font-semibold print:text-amber-700">{item.date}</div>
                  <div className="flex-1">
                    <p className="text-white font-medium print:text-black">{item.phase}</p>
                    <p className="text-gray-400 text-sm print:text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-amber-900/50 to-red-900/50 rounded-xl p-8 text-center print:bg-amber-50 print:border print:border-amber-300">
            <h2 className="text-2xl font-bold text-white mb-4 print:text-black">Conclusion</h2>
            <p className="text-gray-200 leading-relaxed mb-6 print:text-gray-700">
              The Carnival Cultures Interactive Tour Museum represents a <strong>historic opportunity</strong> for the City of New Orleans to preserve authentic carnival traditions, educate residents and visitors, unite diverse communities, attract cultural tourism revenue, and empower community members with real skills.
            </p>
            <p className="text-xl text-amber-400 font-semibold italic print:text-amber-700">
              "We call New Orleans a melting pot. It's time to show the world what's in the pot."
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-12">
          <div className="bg-[#1a1a1a] rounded-xl p-6 text-center print:bg-gray-100 print:border">
            <h2 className="text-xl font-bold text-white mb-4 print:text-black">Contact Information</h2>
            <p className="text-amber-400 font-semibold text-lg print:text-amber-700">Mestre Curtis Pierre</p>
            <p className="text-gray-300 print:text-gray-700">Founder & Cultural Director, Escola de Samba Casa Samba</p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <Globe className="w-4 h-4 text-gray-400" />
              <a href="https://casa-samba-music.preview.emergentagent.com/legacy" className="text-amber-400 hover:underline print:text-amber-700">
                View Full Legacy & Credentials
              </a>
            </div>
          </div>
        </section>

        {/* Footer Quote */}
        <div className="text-center text-gray-500 italic print:text-gray-600">
          "Born in New Orleans, shaped by Brasil, Mestre Curtis Pierre bridges two worlds as the sole American to master both the ancient art of Capoeira and the soul-stirring rhythms of Brasilian percussion."
        </div>

      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          body {
            margin: 0;
            padding: 20px;
          }
          .min-h-screen {
            min-height: auto !important;
          }
          .bg-\\[\\#0a0a0a\\], .bg-\\[\\#1a1a1a\\], .bg-\\[\\#252525\\] {
            background: #f5f5f5 !important;
          }
          .text-white, .text-gray-300, .text-gray-400 {
            color: #333 !important;
          }
          .text-amber-400 {
            color: #b45309 !important;
          }
          .text-green-400 {
            color: #047857 !important;
          }
          .text-blue-400 {
            color: #1d4ed8 !important;
          }
          .text-red-400 {
            color: #b91c1c !important;
          }
          .text-purple-400 {
            color: #7c3aed !important;
          }
          .bg-gradient-to-r, .bg-gradient-to-b {
            background: #fef3c7 !important;
          }
          .border {
            border: 1px solid #d1d5db !important;
          }
          .rounded-xl, .rounded-lg {
            border-radius: 8px !important;
          }
          a {
            color: #b45309 !important;
            text-decoration: underline;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            margin: 0.5in;
            size: letter;
          }
        }
      `}</style>
    </div>
  );
};


import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const MuseumProposal = () => (
  <>
    <MuseumProposalInner />
    <AdminEditorLink to="/admin/live-editor" label="Edit via Site Editor" />
  </>
);

export default MuseumProposal;
